import { useQuery } from "@tanstack/react-query";
import { useEvent } from "../context/EventProvider";
import { fetchConferences, type Conference } from "./conferences";
import { useParticipation } from "./useParticipation";

/**
 * LES CONFÉRENCES DE CE PARTICIPANT — une lecture, deux écrans.
 *
 * L'étape les propose, le récapitulatif nomme celle qui a été retenue. Même clé de
 * requête, donc une seule requête : arriver à la confirmation ne redemande pas ce que
 * l'étape vient de lire.
 *
 * ⚠️ ELLES SONT PROPRES À LA PERSONNE, pas à l'événement — et c'est ce qui les
 * distingue des jours et des typologies. La route ne rend pas un catalogue : elle rend
 * ce que CE participant peut choisir, filtré par les catégories de sa société, avec
 * ses verrous déjà calculés. Il faut donc une session, et le fil du tunnel ne peut pas
 * s'en servir pour décider si l'étape existe (voir `funnel/thread.ts`).
 */
export function useConferences(
  /**
   * Faut-il seulement demander ? Le récapitulatif ne le fait que si l'étape est du
   * parcours : une requête sur chaque confirmation de chaque événement, dont
   * l'immense majorité n'a pas de conférences, pour une section qui ne s'afficherait
   * jamais.
   */
  active: boolean,
): {
  conferences: ReadonlyArray<Conference>;
  /** Combien il peut en retenir ; `null` = aucune limite. */
  max: number | null;
  /** Celles où ce participant a un créneau. */
  selected: ReadonlyArray<Conference>;
  loading: boolean;
  /** La liste n'a pas pu être lue — dit, plutôt que rendu en « aucune conférence ». */
  failure: boolean;
} {
  const event = useEvent();
  const { me, unavailable } = useParticipation();

  const q = useQuery({
    queryKey: ["conferences", event?.domainId, event?.eventId, me?.id],
    queryFn: ({ signal }) =>
      fetchConferences(event!.domainId, event!.eventId, me!.id, signal),
    enabled: active && !!event && typeof me?.id === "number",
    /* ⚠️ PAS `Infinity`, contrairement aux formulaires et aux sociétés. Les places se
       prennent PENDANT que le participant regarde l'écran : une liste figée pour la
       visite lui montrerait « Disponible » sur un créneau que le serveur vient de
       refuser à quelqu'un d'autre. Elle est donc relue en revenant sur l'étape. */
    staleTime: 30_000,
    retry: false,
  });

  /**
   * ⚠️ RIEN SANS L'ÉTAPE, MÊME SI LE CACHE A ENCORE QUELQUE CHOSE (bug #238). Éteinte,
   * la requête ne part plus, mais `useQuery` rend toujours la réponse d'une visite
   * d'avant. Et il y en a une : qui vient de passer à une typologie sans conférences a
   * réservé sous l'ancienne. Relevé à la simulation : le récapitulatif listait encore
   * la séance que l'écriture venait de rendre (`api-v1/api/helpers/cartOnTypoChange.js`),
   * jusqu'au rechargement. Même règle que la commande, dans `Recap.tsx`.
   */
  const data = active ? q.data : undefined;
  const conferences = data?.conferences ?? [];
  return {
    conferences,
    max: data?.max ?? null,
    selected: conferences.filter((c) => c.selected),
    // Identité illisible (échec non-401 de « qui suis-je ») : la requête reste désactivée,
    // donc `isPending` sans fin. On le traduit en échec, pas en chargement perpétuel.
    loading: active && q.isPending && !unavailable,
    failure: q.isError || unavailable,
  };
}
